import React, { useState } from "react";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    setSubmitStatus(null);
    setErrorMessage("");
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data?.error || "Error");
      setSubmitStatus("success");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setSubmitStatus("error");
      setErrorMessage(err?.message || "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Color Constants (matching your image)
  const colors = {
    dark: "#121212",
    beige: "#F5F5F1",
    inputBg: "#F9F9F7",
    placeholder: "#9CA3AF"
  };

  return (
    <section id="contact" className="py-20 px-6" style={{ backgroundColor: colors.dark }}>
      <div className="max-w-3xl mx-auto">
        {/* Heading - Serif font for that premium look */}
        <h2 
          className="text-5xl md:text-6xl font-serif mb-8 text-center" 
          style={{ color: colors.beige }}
        >
          Contact Me
        </h2>
        
        <p 
          className="text-center mb-12 text-lg md:text-xl leading-relaxed opacity-90 font-light" 
          style={{ color: colors.beige }}
        >
          If you require further information or would like to discuss your requirements,
          please complete the form below. I will get back to you within one working day.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <input
              type="text"
              name="name"
              placeholder="Your Name *"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full px-6 py-4 rounded-xl border-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#121212] focus:ring-white transition-all duration-300 outline-none text-gray-800"
              style={{ backgroundColor: colors.inputBg }}
            />
            <input
              type="email"
              name="email"
              placeholder="Your Email *"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full px-6 py-4 rounded-xl border-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#121212] focus:ring-white transition-all duration-300 outline-none text-gray-800"
              style={{ backgroundColor: colors.inputBg }}
            />
          </div>

          <input
            type="tel"
            name="phone"
            placeholder="Phone Number (optional)"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-6 py-4 rounded-xl border-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#121212] focus:ring-white transition-all duration-300 outline-none text-gray-800"
            style={{ backgroundColor: colors.inputBg }}
          />

          <textarea
            name="message"
            placeholder="Tell me about your educational needs and requirements... *"
            required
            rows={5}
            value={formData.message}
            onChange={handleChange}
            className="w-full px-6 py-4 rounded-xl border-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#121212] focus:ring-white transition-all duration-300 outline-none text-gray-800 resize-none"
            style={{ backgroundColor: colors.inputBg }}
          />

          {/* Status Messages */}
          {submitStatus === "success" && (
            <p className="text-center text-green-400 text-sm animate-pulse">
              Message sent successfully! I'll be in touch soon.
            </p>
          )}
          {submitStatus === "error" && (
            <p className="text-center text-red-400 text-sm">
              {errorMessage}
            </p>
          )}

          <div className="text-center pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-black text-white px-14 py-4 rounded-full font-medium text-lg 
                         hover:bg-[#1a1a1a] hover:-translate-y-1 transition-all duration-300 
                         active:scale-95 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed
                         border border-white/10"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </span>
              ) : "Send Message"}
            </button>
          </div>
        </form>

        <div className="mt-16 text-center">
          <p className="text-sm tracking-wide opacity-60 italic" style={{ color: colors.beige }}>
            Or schedule a virtual meeting to discuss your needs.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
